import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography,
} from '@mui/material';
import { ShieldCheck } from 'lucide-react';
import { UserContext } from '../context/UserContext';
import { primaryButtonStyle, secondaryButtonStyle, dialogActionsStyle } from '../utils/sharedStyles';

// The move to Microsoft sign-in (EntraFullMigrationPlan.md §3.1–3.2): a legacy
// session is sent to Microsoft's sign-up page with its email filled in, where it
// verifies the email with a code and chooses a new password, and comes back
// linked to the same account. `blocking`: the user has used every "later" (D18),
// so the only ways on are to move or to sign out.
const EntraMigrationDialog = ({ open, onLater, blocking = false, initialError = '' }) => {
  const { user, loginWithEntra, logout } = useContext(UserContext);
  const [isLeaving, setIsLeaving] = useState(false);
  const [error, setError] = useState(initialError);

  const start = async () => {
    setIsLeaving(true);
    setError('');
    try {
      // A Google account opens Microsoft's sign-in page with nothing filled in,
      // and the text says to click Google there. With the email filled in,
      // Microsoft takes the email path, which asks a Google account for a password
      // it has not. Skipping straight to Google (domain_hint) is not reliable in
      // desktop browsers. `prompt: 'login'` skips Microsoft's picker of accounts
      // already signed in on this browser (another doctor's, on a shared computer).
      await loginWithEntra(user?.loginWithGoogle
        ? { intent: 'migrate', prompt: 'login' }
        : { intent: 'migrate', loginHint: user?.email, prompt: 'create' });
    } catch (startError) {
      console.error('Starting the Microsoft sign-up failed:', startError);
      setError('לא ניתן לעבור כעת לדף הכניסה. נסו שוב מאוחר יותר.');
      setIsLeaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isLeaving || blocking ? undefined : onLater}
      disableEscapeKeyDown={blocking}
      PaperProps={{ style: { borderRadius: '8px', padding: '16px', width: '100%', maxWidth: '440px' } }}
    >
      <DialogTitle style={{ textAlign: 'center', fontWeight: 'bold', color: '#103C6E' }}>
        <ShieldCheck size={40} color="#1FB5A3" style={{ display: 'block', margin: '0 auto 8px' }} />
        {blocking ? 'נדרש מעבר לכניסה החדשה' : 'שדרגו את אבטחת החשבון'}
      </DialogTitle>
      <DialogContent dir="rtl" style={{ textAlign: 'center' }}>
        <Typography style={{ color: '#103C6E' }}>
          {blocking
            ? 'כדי להמשיך להשתמש במשלום יש לעבור לכניסה המאובטחת החדשה. זה לוקח פחות מדקה.'
            : 'משלום עוברת לכניסה מאובטחת חדשה. זה לוקח פחות מדקה.'}
        </Typography>
        <Typography style={{ marginTop: '12px', color: '#103C6E' }}>
          המייל שלכם במשלום:
          <span dir="ltr" style={{ display: 'block', fontWeight: 'bold' }}>{user?.email}</span>
        </Typography>
        <Typography variant="body2" color="text.secondary" style={{ marginTop: '12px' }}>
          {user?.loginWithGoogle
            ? 'בדף הבא לחצו על "Google" ובחרו את החשבון הזה. אין צורך להקליד את כתובת המייל.'
            : 'בדף הבא תקבלו קוד לכתובת הזו ותבחרו סיסמה חדשה.'}
          {' '}החשבון, ההרשאות והמחלקות שלכם נשארים כמו שהם.
        </Typography>
        {!user?.loginWithGoogle && (
          // On a computer where someone already signed in with Microsoft, Microsoft
          // shows its account list before the sign-up page, and "another account"
          // leads to the sign-in page without the email (EntraFullMigrationPlan.md §3.1).
          <Typography variant="body2" color="text.secondary" style={{ marginTop: '8px' }}>
            אם מופיעה רשימת חשבונות: בחרו &quot;השתמש בחשבון אחר&quot;, אחר כך &quot;עוד לא נרשמת?&quot;, והזינו את המייל שלמעלה.
          </Typography>
        )}
        {error && (
          <Typography color="error" role="alert" style={{ marginTop: '12px' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions style={dialogActionsStyle}>
        {blocking ? (
          <Button onClick={() => logout()} style={secondaryButtonStyle} disabled={isLeaving}>
            התנתקות
          </Button>
        ) : (
          <Button onClick={onLater} style={secondaryButtonStyle} disabled={isLeaving}>
            לא עכשיו
          </Button>
        )}
        <Button onClick={start} style={primaryButtonStyle} disabled={isLeaving}>
          {isLeaving ? 'מעביר...' : 'התחילו עכשיו'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EntraMigrationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onLater: PropTypes.func.isRequired,
  blocking: PropTypes.bool,
  initialError: PropTypes.string,
};

export default EntraMigrationDialog;
